import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Clock, Mail, LogOut, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_sgficapital/artifacts/i5d6sap9_LOGO%20SGFI-nuevo.png';

export default function PendingApproval() {
  const { user, logout, fetchUser } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [checking, setChecking] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleRefresh = async () => {
    setChecking(true);
    try {
      await fetchUser();
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d1218] flex flex-col items-center justify-center p-4" data-testid="pending-approval-page">
      <div className="max-w-md w-full text-center">
        {/* Logo */}
        <img src={LOGO_URL} alt="SGFI Capital" className="h-12 w-auto mx-auto mb-8" />
        
        {/* Icon */}
        <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
          <Clock className="w-10 h-10 text-amber-400" />
        </div>
        
        {/* Title */}
        <h1 className="text-2xl font-bold text-white mb-3">
          {t('pendingApproval.title')}
        </h1>
        
        {/* Description */}
        <p className="text-slate-400 mb-2">
          {t('pendingApproval.thanksForRegistering')}, <span className="text-white font-medium">{user?.name}</span>!
        </p>
        <p className="text-slate-400 mb-8">
          {t('pendingApproval.accountBeingReviewed')}
        </p>
        
        {/* Email Info */}
        <div className="bg-white/5 rounded-xl p-4 mb-8 border border-white/10">
          <div className="flex items-center justify-center gap-2 text-slate-300">
            <Mail className="w-5 h-5 text-emerald-400" />
            <span>{user?.email}</span>
          </div>
        </div>
        
        {/* Status Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/20 text-amber-400 text-sm font-medium mb-8">
          <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
          {t('pendingApproval.status')}
        </div>
        
        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleRefresh}
            disabled={checking}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="check-status-btn"
          >
            {checking ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            {t('pendingApproval.checkStatus')}
          </Button>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="flex-1 border-white/20 text-slate-300 hover:bg-white/5"
            data-testid="logout-pending-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t('pendingApproval.logout')}
          </Button>
        </div>
        
        {/* Help Text */}
        <p className="text-slate-500 text-sm mt-8">
          {t('pendingApproval.helpText')}{' '}
          <a href="mailto:info@sgfi.capital" className="text-emerald-400 hover:underline">
            info@sgfi.capital
          </a>
        </p>
      </div>
    </div>
  );
}
